import { type EditorView } from 'prosemirror-view'
import { Plugin, PluginKey, TextSelection } from 'prosemirror-state'

import { getNextTextPosition } from 'common/Prose/utils'
import { serializeForClipboard } from './clipboard'
import { StrongNodeType } from 'prose-core'

type nodeWithOptions = {
  type: StrongNodeType<any, any>
  /**
   * Whether to respect the slice generated by prosemirror when serializing for clipboard, or force it to be closed.
   * Setting this to true seems to help when pasting boxes and quotes, otherwise prosemirror will discard the wrapping
   * div and paste just the contents
   */
  forceCloseSlice?: boolean
}

const extendSelection =
  (_nodeTypes: Array<StrongNodeType<any, any> | nodeWithOptions>) =>
  (view: EditorView, event: ClipboardEvent, deleteSelection: boolean) => {
    const {
      state: {
        doc,
        selection: { $from, $to, empty, from, to },
        tr,
      },
    } = view
    const nodeTypes = _nodeTypes.map((nodeType) => {
      if ((nodeType as any).type) {
        return nodeType as nodeWithOptions
      } else {
        return {
          type: nodeType as StrongNodeType<any, any>,
        }
      }
    })
    if (empty) return false
    const matchingNodeType = nodeTypes.find((nt) => nt.type === $from.node($from.depth - 1)?.type)

    let extendedFrom: number | null = null
    let extendedTo: number | null = null

    if (
      $from.pos === $from.start() &&
      matchingNodeType &&
      $from.node() === $from.node($from.depth - 1)?.firstChild &&
      $to.pos >= $from.after($from.depth - 1) - 2
    ) {
      extendedFrom = $from.before($from.depth - 1)
    }

    if (
      $to.pos === $to.end() &&
      matchingNodeType &&
      $to.node() === $to.node($to.depth - 1)?.lastChild &&
      $to.depth > 1 &&
      $from.pos <= getNextTextPosition(view.state.doc.resolve($to.before($to.depth - 1))).pos
    ) {
      extendedTo = $to.after($to.depth - 1)
    }

    if (!extendedFrom && !extendedTo) return false

    let selection = TextSelection.create(doc, extendedFrom || from, extendedTo || to)

    const { dom, text } = serializeForClipboard(view, selection.content(), {
      forceCloseSlice: matchingNodeType && matchingNodeType.forceCloseSlice === true ? true : false,
    })
    event.clipboardData?.setData('text/html', dom.innerHTML)
    event.clipboardData?.setData('text', text)

    if (deleteSelection) {
      view.dispatch(tr.deleteRange(extendedFrom || from, extendedTo || to))
    }

    event.preventDefault()
    return true
  }

const ExtendSelectionPluginKey = new PluginKey('ExtendSelection')
export const ExtendSelection = (nodeTypes: Parameters<typeof extendSelection>[0]) =>
  new Plugin({
    key: ExtendSelectionPluginKey,
    props: {
      handleDOMEvents: {
        copy: (view: EditorView, event: Event) => extendSelection(nodeTypes)(view, event as ClipboardEvent, false),
        cut: (view: EditorView, event: Event) => extendSelection(nodeTypes)(view, event as ClipboardEvent, true),
      },
    },
  })
